(() => {
  const GLOBAL_KEY = 'SixteaHeroSlideshow';

  class SixteaHeroSlideshow {
    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll('[data-sixtea-slide]'));
      this.controls = root.querySelector('[data-sixtea-slideshow-controls]');
      this.previousButton = root.querySelector('[data-sixtea-slide-previous]');
      this.nextButton = root.querySelector('[data-sixtea-slide-next]');
      this.dotsContainer = root.querySelector('[data-sixtea-slide-dots]');
      this.duration = Math.max(1000, Number(root.dataset.slideDuration) || 5000);
      this.autoplay = root.dataset.slideAutoplay === 'true';
      this.pauseOnHover = root.dataset.slidePauseHover === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.timer = null;
      this.isHovering = false;
      this.isEditorPaused = false;
      this.bound = {};

      if (this.slides.length <= 1) {
        if (this.controls) this.controls.hidden = true;
        return;
      }

      this.createDots();
      this.bindEvents();
      this.show(this.index, false);
      this.start();
    }

    createDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      this.dots = this.slides.map((_, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sixtea-hero__slide-dot';
        button.setAttribute('aria-label', `Show slide ${index + 1} of ${this.slides.length}`);
        button.addEventListener('click', () => {
          this.show(index, true);
          this.restart();
        });
        this.dotsContainer.appendChild(button);
        return button;
      });
    }

    bindEvents() {
      this.bound.previous = () => {
        this.show(this.index - 1, true);
        this.restart();
      };
      this.bound.next = () => {
        this.show(this.index + 1, true);
        this.restart();
      };
      this.bound.mouseEnter = () => {
        this.isHovering = true;
        if (this.pauseOnHover) this.stop();
      };
      this.bound.mouseLeave = () => {
        this.isHovering = false;
        this.start();
      };
      this.bound.visibility = () => {
        if (document.hidden) this.stop();
        else this.start();
      };
      this.bound.blockSelect = (event) => {
        const blockId = event.detail && event.detail.blockId;
        if (!blockId) return;
        const targetIndex = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
        if (targetIndex < 0) return;
        this.isEditorPaused = true;
        this.stop();
        this.show(targetIndex, false);
      };
      this.bound.blockDeselect = () => {
        this.isEditorPaused = false;
        this.start();
      };

      if (this.previousButton) this.previousButton.addEventListener('click', this.bound.previous);
      if (this.nextButton) this.nextButton.addEventListener('click', this.bound.next);
      if (this.pauseOnHover) {
        this.root.addEventListener('mouseenter', this.bound.mouseEnter);
        this.root.addEventListener('mouseleave', this.bound.mouseLeave);
      }
      document.addEventListener('visibilitychange', this.bound.visibility);
      this.root.addEventListener('shopify:block:select', this.bound.blockSelect);
      this.root.addEventListener('shopify:block:deselect', this.bound.blockDeselect);
    }

    show(nextIndex, announce) {
      const count = this.slides.length;
      this.index = ((nextIndex % count) + count) % count;

      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      if (this.dots) {
        this.dots.forEach((dot, index) => {
          dot.setAttribute('aria-current', index === this.index ? 'true' : 'false');
        });
      }

      if (announce && this.dots && this.dots[this.index]) {
        this.dots[this.index].setAttribute('aria-label', `Current slide ${this.index + 1} of ${count}`);
      }
    }

    start() {
      if (!this.autoplay || this.reducedMotion || this.isEditorPaused || document.hidden) return;
      if (this.pauseOnHover && this.isHovering) return;
      this.stop();
      this.timer = window.setTimeout(() => {
        this.show(this.index + 1, false);
        this.start();
      }, this.duration);
    }

    stop() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }

    restart() {
      this.stop();
      this.start();
    }

    destroy() {
      this.stop();
      if (this.previousButton) this.previousButton.removeEventListener('click', this.bound.previous);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.bound.next);
      if (this.pauseOnHover) {
        this.root.removeEventListener('mouseenter', this.bound.mouseEnter);
        this.root.removeEventListener('mouseleave', this.bound.mouseLeave);
      }
      document.removeEventListener('visibilitychange', this.bound.visibility);
      this.root.removeEventListener('shopify:block:select', this.bound.blockSelect);
      this.root.removeEventListener('shopify:block:deselect', this.bound.blockDeselect);
    }
  }

  const initRoot = (root) => {
    if (!root || !root.matches('[data-sixtea-hero-slideshow]')) return;
    if (root.__sixteaHeroSlideshow) root.__sixteaHeroSlideshow.destroy();
    root.__sixteaHeroSlideshow = new SixteaHeroSlideshow(root);
  };

  const initAll = (scope = document) => {
    if (scope.matches && scope.matches('[data-sixtea-hero-slideshow]')) initRoot(scope);
    scope.querySelectorAll?.('[data-sixtea-hero-slideshow]').forEach(initRoot);
  };

  if (!window[GLOBAL_KEY]) {
    window[GLOBAL_KEY] = { initAll };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initAll());
    } else {
      initAll();
    }

    document.addEventListener('shopify:section:load', (event) => initAll(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      const root = event.target.querySelector?.('[data-sixtea-hero-slideshow]') ||
        (event.target.matches?.('[data-sixtea-hero-slideshow]') ? event.target : null);
      if (root && root.__sixteaHeroSlideshow) root.__sixteaHeroSlideshow.destroy();
    });
  } else {
    window[GLOBAL_KEY].initAll();
  }
})();
